/**
 * ManagerTrainingOverviewPage.jsx — C3c: lentes agregadas de treino do GESTOR.
 *
 * Duas lentes sobre o MESMO dado (1 endpoint, agregação client-side):
 *   POR CATEGORIA      — Infantil/Juvenil/Absoluto... do clube
 *   POR GRUPO DE STAFF — os atletas da Ana (coach), do Bruno (prep)...
 * Cada grupo expande → lista de atletas → clique = drill-down pra
 * /staff/training?athlete=ID (a timeline C3a/C3b, read-only pro gestor
 * via viewer do servidor — zero permissão reimplementada aqui).
 *
 * Dados: GET /vukz/training/manager/training-overview
 *   - GESTOR only; club derivado do ROLE no backend (zero input de escopo)
 *   - aderência REAL (training_plans athlete-cêntrico, completed/total)
 *   - ACWR/risco da vw_athlete_load_summary — fonte physical_load_entries
 *     HOJE vazia no demo → NULL: mostramos '—' honesto (fotografado 05/07;
 *     o cano execução→carga é da fase 'popular Acqua'; quando o dado
 *     entrar, a coluna acende sem mudança aqui).
 *
 * UI_PADRAO: §1 cinza estrito — cor SÓ clínica (risk high/moderate é
 * sinal clínico → âmbar/vermelho discretos permitidos); §3 sentence case,
 * sem emoji; §8b PageLayout + grupos recolhíveis (mesmo padrão de chevron
 * da timeline).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronDown, Loader2, Users, Layers, ClipboardList,
} from 'lucide-react';
import { VUKZ } from '../theme/vukzTheme';
import { AZURE } from '../services/api';
import PageLayout from '../components/layout/PageLayout';

const PAGE_HEADER = {
  title: 'Treino do clube',
  subtitle: 'Aderência e carga por categoria e por grupo de staff',
};

const pct = (done, total) => (total > 0 ? Math.round((done / total) * 100) : null);
const fmtPct = (v) => (v == null ? '—' : `${v}%`);
const fmtNum = (v) => (v == null ? '—' : v);

/* Risco é sinal CLÍNICO — única cor permitida (§1). */
const RISK_META = {
  high:     { label: 'Alto',     bg: '#fef2f2', fg: '#b91c1c' },
  moderate: { label: 'Moderado', bg: '#fffbeb', fg: '#b45309' },
};
const RiskBadge = ({ level }) => {
  const meta = RISK_META[level];
  if (!meta) return <span className="text-[10px]" style={{ color: VUKZ.textLight }}>—</span>;
  return (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
      style={{ backgroundColor: meta.bg, color: meta.fg }}>
      {meta.label}
    </span>
  );
};

/* ─── Linha de atleta — clique = drill-down pra timeline (C3a/C3b) ─── */
const AthleteRow = ({ a }) => {
  const navigate = useNavigate();
  const adherence = pct(a.plans_completed, a.plans_total);
  return (
    <button
      type="button"
      onClick={() => navigate(`/staff/training?athlete=${a.athlete_user_id}`)}
      className="w-full grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3 py-2 text-left hover:bg-neutral-50 transition-colors"
    >
      <span className="text-xs truncate" style={{ color: VUKZ.text }}>
        {a.athlete_name}
        {a.category ? (
          <span className="ml-2 text-[10px]" style={{ color: VUKZ.textLight }}>{a.category}</span>
        ) : null}
      </span>
      <span className="text-[11px] font-mono w-16 text-right" style={{ color: VUKZ.text }}>
        {a.plans_total > 0 ? `${a.plans_completed}/${a.plans_total}` : '—'}
      </span>
      <span className="text-[11px] font-mono w-12 text-right" style={{ color: VUKZ.text }}>
        {fmtNum(a.acwr)}
      </span>
      <span className="w-20 text-right"><RiskBadge level={a.risk_level} /></span>
    </button>
  );
};

/* ─── Grupo recolhível (categoria OU staff) — mesmo chevron da timeline ─── */
const GroupSection = ({ title, subtitle, athletes, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const agg = useMemo(() => {
    const t = athletes.reduce((s, a) => s + (a.plans_total || 0), 0);
    const d = athletes.reduce((s, a) => s + (a.plans_completed || 0), 0);
    const high = athletes.filter((a) => a.risk_level === 'high').length;
    const mod = athletes.filter((a) => a.risk_level === 'moderate').length;
    return { adherence: pct(d, t), high, mod };
  }, [athletes]);

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-neutral-50 text-left group"
      >
        <span className="text-xs font-semibold" style={{ color: VUKZ.text }}>{title}</span>
        {subtitle && <span className="text-[10px]" style={{ color: VUKZ.textLight }}>{subtitle}</span>}
        <span className="ml-auto flex items-center gap-3">
          <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
            {athletes.length} atleta{athletes.length !== 1 ? 's' : ''}
          </span>
          <span className="text-[10px] font-mono" style={{ color: VUKZ.text }}>
            aderência {fmtPct(agg.adherence)}
          </span>
          {(agg.high > 0 || agg.mod > 0) && (
            <span className="text-[10px]" style={{ color: agg.high > 0 ? '#b91c1c' : '#b45309' }}>
              {agg.high > 0 ? `${agg.high} alto risco` : `${agg.mod} moderado`}
            </span>
          )}
          <span className="p-1 rounded-md group-hover:bg-neutral-100 transition-colors">
            {open
              ? <ChevronDown size={14} style={{ color: VUKZ.textLight }} />
              : <ChevronRight size={14} style={{ color: VUKZ.textLight }} />}
          </span>
        </span>
      </button>
      {open && (
        <div>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3 py-1.5 border-t border-b bg-white"
            style={{ borderColor: VUKZ.border }}>
            <span className="text-[9px] uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Atleta</span>
            <span className="text-[9px] uppercase tracking-wide w-16 text-right" style={{ color: VUKZ.textLight }}>Feitos</span>
            <span className="text-[9px] uppercase tracking-wide w-12 text-right" style={{ color: VUKZ.textLight }}>ACWR</span>
            <span className="text-[9px] uppercase tracking-wide w-20 text-right" style={{ color: VUKZ.textLight }}>Risco</span>
          </div>
          <div className="divide-y" style={{ borderColor: VUKZ.border }}>
            {athletes.map((a) => <AthleteRow key={a.athlete_user_id} a={a} />)}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════ */
const ManagerTrainingOverviewPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lens, setLens] = useState('category'); // 'category' | 'staff'

  useEffect(() => {
    let alive = true;
    AZURE.get('/vukz/training/manager/training-overview', { params: { days: 7 } })
      .then((res) => { if (alive) setData(res.data?.data || null); })
      .catch((e) => { console.error('[ManagerOverview] load error:', e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const byCategory = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const a of data.athletes) {
      const key = a.category || 'Sem categoria';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    return [...map.entries()].sort((x, y) => x[0].localeCompare(y[0]));
  }, [data]);

  const byStaff = useMemo(() => {
    if (!data) return [];
    const athleteById = new Map(data.athletes.map((a) => [a.athlete_user_id, a]));
    const map = new Map();
    for (const l of data.staff_links) {
      const a = athleteById.get(l.athlete_user_id);
      if (!a) continue;
      if (!map.has(l.staff_user_id)) {
        map.set(l.staff_user_id, {
          name: l.staff_name,
          role: l.role_key === 'TREINADOR' ? 'Treinador' : 'Preparador físico',
          athletes: [],
        });
      }
      map.get(l.staff_user_id).athletes.push(a);
    }
    return [...map.values()].sort((x, y) => x.name.localeCompare(y.name));
  }, [data]);

  if (loading) {
    return (
      <PageLayout header={PAGE_HEADER}>
        <div className="flex items-center justify-center h-64">
          <Loader2 size={24} className="animate-spin" style={{ color: VUKZ.textLight }} />
        </div>
      </PageLayout>
    );
  }

  if (!data || data.athletes.length === 0) {
    return (
      <PageLayout header={PAGE_HEADER}>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <ClipboardList size={32} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
          <p className="text-sm font-medium" style={{ color: VUKZ.text }}>
            Nenhum atleta no escopo do clube.
          </p>
        </div>
      </PageLayout>
    );
  }

  const groups = lens === 'category'
    ? byCategory.map(([cat, athletes]) => ({ key: cat, title: cat, subtitle: null, athletes }))
    : byStaff.map((s) => ({ key: s.name, title: s.name, subtitle: s.role, athletes: s.athletes }));

  return (
    <PageLayout header={PAGE_HEADER}>
      <div className="max-w-3xl mx-auto">
        {/* Alternador de lente — §8b: segmentado cinza, sem cor decorativa */}
        <div className="flex items-center gap-1 mb-4 p-1 rounded-lg w-fit" style={{ backgroundColor: '#f3f4f6' }}>
          {[
            { id: 'category', label: 'Por categoria', icon: Layers },
            { id: 'staff', label: 'Por grupo de staff', icon: Users },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setLens(opt.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              style={lens === opt.id
                ? { backgroundColor: '#ffffff', color: VUKZ.text, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }
                : { color: VUKZ.textLight }}
            >
              <opt.icon size={13} />
              {opt.label}
            </button>
          ))}
        </div>

        <p className="text-[10px] mb-3" style={{ color: VUKZ.textLight }}>
          Últimos {data.period_days} dias · aderência = treinos concluídos / prescritos ·
          ACWR e risco acendem quando houver registro de carga
        </p>

        <div className="space-y-3">
          {groups.map((g) => (
            <GroupSection key={g.key} title={g.title} subtitle={g.subtitle} athletes={g.athletes} />
          ))}
        </div>
      </div>
    </PageLayout>
  );
};

export default ManagerTrainingOverviewPage;
